import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { VolunteerLayout } from '@layouts/VolunteerLayout/VolunteerLayout';
import { Heading } from '@acb_devel/design-system';
import { Section } from '@layouts/Section/Section';
import { ProfileForm } from '@components/volunteers/organisms/ProfileForm/ProfileForm';
import { createProfile } from '@services/profiles';
import { createRegistration } from '@services/registrations';
import { useUserContext } from '@contexts/UserContext';
import { Button } from '@acb_devel/design-system';
import type { UserProfileForm } from '@components/volunteers/organisms/ProfileForm/ProfileForm';
import { ToastRoot } from '@acb_devel/design-system';
import { useToastContext } from '@acb_devel/design-system';
import { Alert } from '@acb_devel/design-system';

import image1 from '@assets/images/profile-created-successfully-background-images/image-1.webp';
import image2 from '@assets/images/profile-created-successfully-background-images/image-2.webp';
import image3 from '@assets/images/profile-created-successfully-background-images/image-3.webp';
import image4 from '@assets/images/profile-created-successfully-background-images/image-4.webp';
import image5 from '@assets/images/profile-created-successfully-background-images/image-5.webp';
import image6 from '@assets/images/profile-created-successfully-background-images/image-6.webp';

import styles from './CreateProfilePage.module.scss';

export default function RegisterPage() {
  const { user, setUser } = useUserContext();
  const navigate = useNavigate();
  const [showSuccessPage, setShowSuccessPage] = useState<boolean>(false);
  const { addToast } = useToastContext();

  useEffect(() => {
    if (user?.profile && showSuccessPage === false) {
      navigate('/user/edit-profile');
    }
  }, [user, navigate, showSuccessPage]);

  function createProfileSubmit(userProfile: UserProfileForm) {
    if (!user) {
      return;
    }

    createProfile(userProfile)
      .then((profile) => {
        setUser({
          ...user,
          profile,
        });

        setShowSuccessPage(true);
      })
      .catch((error) => {
        addToast(
          <ToastRoot>
            <Alert
              variant="error"
              direction="column"
              title={`Error al crear el perfil`}
              text="Ha ocurrido un error al crear tu perfil. Por favor, inténtalo de nuevo."
            />
          </ToastRoot>
        );

        console.error(error);
      });

    if (userProfile.event) {
      createRegistration({
        event: Number(userProfile.event),
        preferredEventAreas:
          userProfile.areas?.map((area) => Number(area)) ?? [],
      }).catch((error) => {
        console.error(error);
      });
    }
  }

  return (
    <VolunteerLayout showProfileDropdown={false}>
      {showSuccessPage ? (
        <Section className={styles.successSection}>
          <div className={styles.successSection__backgroundImages}>
            <img
              src={image1}
              alt=""
              className={styles.successSection__image1}
            />
            <img
              src={image2}
              alt=""
              className={styles.successSection__image2}
            />
            <img
              src={image3}
              alt=""
              className={styles.successSection__image3}
            />
            <img
              src={image4}
              alt=""
              className={styles.successSection__image4}
            />
            <img
              src={image5}
              alt=""
              className={styles.successSection__image5}
            />
            <img
              src={image6}
              alt=""
              className={styles.successSection__image6}
            />
          </div>
          <div className={styles.successSection__backdrop} />
          <div className={styles.successSection__card}>
            <div className={styles.successSection__cardHeader}>
              <Heading
                as="h2"
                variant="display-3"
                className={styles.successSection__cardTitle}
              >
                ¡Genial! Hemos recibido tu inscripción
              </Heading>
              <Heading
                as="p"
                variant="highlight"
                className={styles.successSection__cardSubtitle}
              >
                En breve recibirás un email de confirmación
              </Heading>
            </div>
            <Link to="/user/profile">
              <Button variant="solid" color="primary" size="2xl">
                Ver mi perfil
              </Button>
            </Link>
          </div>
        </Section>
      ) : (
        <Section className={styles.registerSection}>
          <Heading as="h1" variant="highlight">
            Completa los pasos para inscribirte
          </Heading>
          <ProfileForm onSubmit={createProfileSubmit} defaultValues={user} />
        </Section>
      )}
    </VolunteerLayout>
  );
}
